/**
 * The `supplier_collected` payment mode, as the storefront sees it (KAN-127).
 *
 * A `direct_integration` supplier in this mode keeps the billing relationship
 * with the salon: the order still flows through SalonStocker (unified cart, live
 * pricing, live shipping) and into the supplier's own store, but the **supplier
 * bills the salon directly** - SalonStocker never takes the money. The checkout
 * therefore initiates a dedicated no-capture payment session instead of Stripe,
 * shows an informational panel instead of a card form, and refuses to combine a
 * supplier-collected cart with a marketplace-collected one in a single checkout.
 *
 * Kept out of `lib/payment.tsx` on purpose: that module calls `loadStripe` at
 * module scope, and the cart-page code that reads these predicates has no
 * business pulling Stripe's script loader in behind it - the same reason
 * `isBankTransfer` lives in `lib/bank-transfer`.
 */

import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * The id of the dedicated `supplier-collected` payment provider (api KAN-128).
 *
 * Medusa forms a provider id as `pp_<identifier>_<config-id>`; this provider is
 * registered with identifier and id both `supplier-collected`. It is
 * deliberately **not** region-enabled, so it never appears in
 * `GET /store/payment-providers` and is never a selectable radio - the checkout
 * forces it by this hardcoded id for supplier-collected carts, and the
 * initiation route has no region check to stand in the way (KAN-131 / R3-B1).
 */
export const SUPPLIER_COLLECTED_PROVIDER_ID =
  "pp_supplier-collected_supplier-collected";

/**
 * The seller flags the payment-mode routing reads, and the cart id it keys on.
 * A full {@link SalonStockerHttpTypes.StoreSellerCart} satisfies this, so
 * callers pass their carts straight through while tests build only these fields.
 */
type PaymentModeCart = Pick<
  SalonStockerHttpTypes.StoreSellerCart,
  "cart_id"
> & {
  seller: Pick<
    SalonStockerHttpTypes.StoreSellerCartSellerInfo,
    "supports_external_checkout" | "payment_collected_by_supplier"
  >;
};

/**
 * Whether this checkout is a supplier-collected one.
 *
 * Homogeneity is guaranteed upstream - the cart page blocks a mixed "Checkout
 * all" ({@link resolveCombinedCheckout}) and the checkout page refuses a mixed
 * URL - so every cart here shares a mode. `.some` rather than `.every` is the
 * deliberate fail-safe if that guarantee is ever breached: treating the group as
 * supplier-collected suppresses Stripe, so the worst case is a marketplace cart
 * left uncollected (recoverable), never a real card charge for a supplier who
 * bills the salon directly (a double charge, and the exact bug the mode exists
 * to avoid).
 */
export function isSupplierCollectedCheckout(
  carts: readonly PaymentModeCart[]
): boolean {
  return carts.some((cart) => cart.seller.payment_collected_by_supplier);
}

/**
 * Whether a set of carts spans **both** payment modes - at least one
 * supplier-collected and at least one marketplace-collected. Such a set cannot
 * check out together: one cart takes no card and the other must.
 */
export function isMixedPaymentModes(
  carts: readonly PaymentModeCart[]
): boolean {
  let sawSupplierCollected = false;
  let sawMarketplace = false;
  for (const cart of carts) {
    if (cart.seller.payment_collected_by_supplier) {
      sawSupplierCollected = true;
    } else {
      sawMarketplace = true;
    }
    if (sawSupplierCollected && sawMarketplace) return true;
  }
  return false;
}

/**
 * Whether this cart belongs to an external-checkout seller (e.g. Shopify), who
 * redirects to their own store instead of the internal checkout.
 */
export function isExternalCheckoutSeller(cart: PaymentModeCart): boolean {
  return Boolean(cart.seller.supports_external_checkout);
}

/**
 * Whether any cart belongs to an external-checkout seller. The cart page hides
 * "Checkout all" when one is among the buyer's carts: a combined checkout
 * cannot cover them, so offering it would only ever order part of what the
 * salon sees.
 */
export function hasExternalCheckoutSeller(
  carts: readonly PaymentModeCart[]
): boolean {
  return carts.some(isExternalCheckoutSeller);
}

/** The outcome of planning a combined ("Checkout all") checkout. */
export type CombinedCheckoutPlan =
  | { ok: true; cartIds: string[] }
  | { ok: false; reason: "mixed_payment_modes" };

/**
 * Plans a combined checkout for a selection of carts: which cart ids travel to
 * the internal checkout, or whether the action must be blocked.
 *
 * Returns the decision rather than acting on it - the caller shows the message -
 * so the branching is testable without standing up the cart page.
 *
 * Two exclusions, deliberately different in kind:
 * - **External-checkout sellers are dropped silently.** They redirect to their
 *   own store and are checked out via their per-supplier button, so they were
 *   never part of an internal "Checkout all" (ADR-0001). The cart page no longer
 *   offers "Checkout all" at all when one is present
 *   ({@link hasExternalCheckoutSeller}); this stays as the guard that keeps them
 *   off the internal checkout (and Stripe) should a caller reach it anyway.
 * - **A mode-mixed selection is blocked, not trimmed.** A supplier-collected
 *   cart and a marketplace cart cannot share one payment step, so rather than
 *   silently drop one mode the whole action is refused and the salon is told to
 *   check those suppliers out separately.
 */
export function resolveCombinedCheckout(
  carts: readonly PaymentModeCart[]
): CombinedCheckoutPlan {
  const internal = carts.filter((cart) => !isExternalCheckoutSeller(cart));

  if (isMixedPaymentModes(internal)) {
    return { ok: false, reason: "mixed_payment_modes" };
  }

  return { ok: true, cartIds: internal.map((cart) => cart.cart_id) };
}

/**
 * The cart page's toast when a combined "Checkout all" is blocked for spanning
 * both payment modes. Shared by the cart page and its test so those two cannot
 * drift.
 *
 * Scope is deliberately just that toast: the checkout page's `MixedCheckoutState`
 * is a different surface (a full-page dead-end reached only by a hand-crafted
 * URL) and carries its own, fuller copy - it is not meant to match this string.
 */
export const MIXED_PAYMENT_MODES_MESSAGE = {
  title: "These suppliers can't be combined",
  description:
    "Some of your suppliers collect payment directly and must be checked out " +
    "on their own. Use each supplier's checkout button to order from them."
} as const;
